import { useMemo } from 'react'

import {
  Badge,
  Button,
  Card,
  Group,
  NumberFormatter,
  Skeleton,
  Stack,
  Switch,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { IconBuilding, IconPlus } from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'

import { usdFormatter } from '@repo/common/helpers/formatters'
import { type Fund, useAdminFunds } from '@repo/common/queries/admin/fund'

import { TowerCard } from '@/components/ui/TowerCard'
import { usePersistedValue } from '@/hooks/usePersistedValue'

import styles from './FundsOverview.module.css'

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface FundsOverviewProps {}

interface FundPanelProps {
  fund: Fund
}

const FundPanel: React.FC<FundPanelProps> = ({ fund }) => {
  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMM D, YYYY')
  }

  const formatDateTime = (dateString: string) => {
    return dayjs(dateString).format('MMM D, YYYY h:mm A')
  }

  return (
    <Card withBorder radius="md" p={0} className={styles.fund_panel}>
      <Link
        to="/fund/$id"
        params={{ id: fund.id }}
        className={styles.fund_link}
      >
        <div className={styles.fund_content}>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap="sm" style={{ flex: 1 }}>
              {/* Fund Header */}
              <Group align="center" gap="xs" className={styles.fund_header}>
                <Title order={5} className={styles.fund_name}>
                  {fund.name}
                </Title>
                <Badge
                  variant="light"
                  size="xs"
                  className={styles.fund_id_badge}
                >
                  {fund.id.toUpperCase()}
                </Badge>
                {(!fund.approve_nav_publish || !fund.approve_nav_official) && (
                  <Badge
                    variant="light"
                    size="xs"
                    className={styles.status_badge_auto_publish}
                  >
                    Auto Publish{' '}
                    {!fund.approve_nav_publish && !fund.approve_nav_official
                      ? 'All'
                      : !fund.approve_nav_publish
                        ? 'Estimated'
                        : 'Official'}
                  </Badge>
                )}
              </Group>

              {/* Key Metrics Row */}
              <Group gap="lg" className={styles.metrics_row}>
                <div className={styles.metric_item}>
                  <Text className={styles.metric_label}>Management Fee</Text>
                  <Text
                    className={`${styles.metric_value} ${styles.fund_fee_highlight}`}
                  >
                    <NumberFormatter
                      value={fund.management_fee_percentage}
                      suffix="%"
                      decimalScale={2}
                    />
                  </Text>
                </div>
                <div className={styles.metric_item}>
                  <Text className={styles.metric_label}>Current PPS</Text>
                  <Text
                    className={`${styles.metric_value} ${styles.fund_nav_highlight}`}
                  >
                    {fund.live_nav
                      ? usdFormatter(fund.live_nav.price_per_share)
                      : 'No NAV'}
                  </Text>
                </div>
                <div className={styles.metric_item}>
                  <Text className={styles.metric_label}>Publication</Text>
                  <Text className={styles.metric_value}>
                    {fund.publication_schedule}
                  </Text>
                </div>
              </Group>

              {/* Secondary Information */}
              <Group gap="md" className={styles.fund_metadata_group}>
                <Text
                  size="xs"
                  c="dimmed"
                  className={styles.fund_secondary_text}
                >
                  Created {formatDate(fund.created_at)}
                </Text>
                {fund.next_publication_at && (
                  <Tooltip label="Next scheduled publication">
                    <Text
                      size="xs"
                      c="dimmed"
                      className={`${styles.fund_secondary_text} ${styles.info_tooltip}`}
                    >
                      Next:{' '}
                      {fund.next_publication_official ? 'Official' : 'Est.'} @{' '}
                      {formatDateTime(fund.next_publication_at)}
                    </Text>
                  </Tooltip>
                )}
              </Group>
            </Stack>

            <Stack gap="xs" align="flex-end" style={{ minWidth: '200px' }}>
              {(fund.feeders?.length || 0) > 0 ? (
                <Stack gap="xs" align="stretch">
                  {fund.feeders?.map((feeder) => (
                    <div key={feeder.id} className={styles.token_group}>
                      <Group gap="xs" align="center" justify="space-between">
                        <Text
                          fw={500}
                          size="sm"
                          className={styles.token_symbol}
                        >
                          {feeder.symbol}
                        </Text>
                        <Group gap="xs">
                          <Tooltip
                            label={
                              feeder.published
                                ? 'Publicly available'
                                : 'Internal only'
                            }
                          >
                            <Badge
                              variant="light"
                              size="xs"
                              className={
                                feeder.published
                                  ? styles.status_badge_public
                                  : styles.status_badge_hidden
                              }
                            >
                              {feeder.published ? 'Public' : 'Hidden'}
                            </Badge>
                          </Tooltip>
                          <Tooltip
                            label={
                              feeder.tradable
                                ? 'Live trading available'
                                : 'Simulation mode only'
                            }
                          >
                            <Badge
                              variant="light"
                              size="xs"
                              className={
                                feeder.tradable
                                  ? styles.status_badge_tradable
                                  : styles.status_badge_simulated
                              }
                            >
                              {feeder.tradable ? 'Tradable' : 'Simulated'}
                            </Badge>
                          </Tooltip>
                        </Group>
                      </Group>
                    </div>
                  ))}
                </Stack>
              ) : (
                <Text
                  size="sm"
                  c="dimmed"
                  ta="center"
                  p="sm"
                  style={{ fontStyle: 'italic' }}
                >
                  No tokens found
                </Text>
              )}
            </Stack>
          </Group>
        </div>
      </Link>
    </Card>
  )
}

const LoadingSkeleton: React.FC = () => (
  <Card withBorder radius="md" p="md">
    <Group justify="space-between" align="center" wrap="nowrap">
      <Group gap="md" align="center">
        <Group gap="xs">
          <Skeleton height={20} width={150} />
          <Skeleton height={16} width={40} />
        </Group>
        <Skeleton height={12} width={60} />
        <Skeleton height={12} width={80} />
        <Skeleton height={12} width={100} />
        <Skeleton height={18} width={60} />
      </Group>

      <Group gap="md" align="center">
        <Group gap="xs">
          <Skeleton height={16} width={40} />
          <Skeleton height={16} width={120} />
          <Skeleton height={16} width={50} />
          <Skeleton height={16} width={60} />
        </Group>
        <Group gap="xs">
          <Skeleton height={16} width={40} />
          <Skeleton height={16} width={100} />
          <Skeleton height={16} width={50} />
          <Skeleton height={16} width={70} />
        </Group>
      </Group>
    </Group>
  </Card>
)

export const FundsOverview: React.FC<FundsOverviewProps> = () => {
  const { data: fundsData, isLoading, error } = useAdminFunds()

  const [showTradableOnly, setShowTradableOnly] = usePersistedValue(
    'funds_overview_tradable',
    false,
  )
  const [showPublishedOnly, setShowPublishedOnly] = usePersistedValue(
    'funds_overview_published',
    false,
  )

  const filteredFunds = useMemo(() => {
    if (!fundsData?.funds.nodes) return []

    return fundsData.funds.nodes.filter((fund) => {
      if (showTradableOnly && !(fund.feeders || []).some((f) => f.tradable)) {
        return false
      }
      if (showPublishedOnly && !(fund.feeders || []).some((f) => f.published)) {
        return false
      }
      return true
    })
  }, [fundsData?.funds.nodes, showTradableOnly, showPublishedOnly])

  if (error) {
    return (
      <TowerCard
        title="Funds Overview"
        description="Error loading funds"
        icon={<IconBuilding size={24} />}
      >
        <Text c="red">Failed to load funds data</Text>
      </TowerCard>
    )
  }

  return (
    <TowerCard
      title="Funds Overview"
      description="All funds and their associated tokens"
      icon={<IconBuilding size={24} />}
    >
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <Group gap="md">
            <Switch
              label="Tradable"
              description="Blockchain integrated"
              checked={showTradableOnly}
              onChange={(event) => {
                setShowTradableOnly(event.currentTarget.checked)
              }}
            />
            <Switch
              label="Published"
              description="Publicly visible in Labs"
              checked={showPublishedOnly}
              onChange={(event) => {
                setShowPublishedOnly(event.currentTarget.checked)
              }}
            />
          </Group>

          <Button
            component={Link}
            href="/funds/add"
            leftSection={<IconPlus size={16} />}
            variant="filled"
          >
            New Fund
          </Button>
        </Group>

        {isLoading ? (
          <Stack gap="lg" className={styles.loading_skeleton_container}>
            {Array.from({ length: 6 }).map((_, index) => (
              <LoadingSkeleton key={index} />
            ))}
          </Stack>
        ) : filteredFunds.length === 0 ? (
          <Text ta="center" c="dimmed" py="xl">
            {showTradableOnly || showPublishedOnly
              ? 'No funds match the selected filters'
              : 'No funds found'}
          </Text>
        ) : (
          <Stack gap="lg" className={styles.funds_list}>
            {filteredFunds.map((fund) => (
              <FundPanel key={fund.id} fund={fund} />
            ))}
          </Stack>
        )}

        {!isLoading && filteredFunds.length > 0 && (
          <Text size="sm" c="dimmed" ta="center">
            Showing {filteredFunds.length} of{' '}
            {fundsData?.funds.total_count || 0} funds
          </Text>
        )}
      </Stack>
    </TowerCard>
  )
}
